/**
 * Protected-ID canonicalization + isomorphic base64url codec.
 *
 * A protected ID is `base64url(HMAC-SHA256(tenantKey, canonicalizeId(rawValue)))`
 * (PRODUCT.md "Protected ID": deterministic, one-way, per-tenant). The WebCrypto
 * HMAC itself lives in the browser-only `client/uploader/` layer; THIS module is
 * the pure, isomorphic part: the value normalization that runs before hashing,
 * and the base64url codec the client uses to import the raw key and emit the
 * digest.
 *
 * Determinism is the whole point. The same logical ID in two snapshots must
 * canonicalize identically so the backend can join on protected IDs without ever
 * seeing raw IDs — getting canonicalization wrong silently splits one loan into
 * two protected IDs. So canonicalization is conservative and fully specified:
 *
 *   - Loan ID:  trim → Unicode NFC → strip all whitespace → case-fold (matching is
 *               case-insensitive) → strip leading zeros, but never to empty (an
 *               all-zeros ID collapses to "0"). Non-whitespace separators (e.g. "-",
 *               "/") and internal/trailing digits are preserved.
 *   - Tax ID:   trim → Unicode NFC → strip every non-alphanumeric character, so
 *               `12-3456789` and `123456789` collapse to the same canonical form.
 *
 * (Loan-ID rule founder-confirmed 2026-07-18: US community-bank / credit-union core
 * loan numbers are effectively case-insensitive and are commonly stored as fixed-width
 * zero-padded fields, so `0012345`, `12345`, `12 345`, and `ABC123`/`abc123` are the
 * same loan. Only leading zeros are dropped — trailing/internal digits stay
 * significant — and a lone separator/leading zero never collapses two genuinely
 * distinct IDs.)
 *
 * No node:* and no browser-only globals: encode/decode operate on plain strings
 * and `Uint8Array`, so the codec is identical in the Worker and the browser.
 */

/** Which protected-ID kind a raw value is, selecting its canonicalization rule. */
export type ProtectedIdKind = 'loan' | 'tax';

/**
 * Canonicalize a raw ID value before HMAC. Determinism contract: equal logical
 * IDs map to the equal canonical string for a given `kind`. Throws nothing;
 * empty/whitespace-only input canonicalizes to the empty string — `computeProtectedId`
 * maps that empty canonical form to an empty protected ID (a blank ID is not an ID,
 * so it is never merged into a loan; see that function's note).
 */
export function canonicalizeId(raw: string, kind: ProtectedIdKind): string {
  // NFC first so combining sequences and pre-composed forms unify before any
  // structural edits (e.g. so whitespace collapse sees normalized characters).
  const normalized = raw.normalize('NFC').trim();
  if (kind === 'tax') {
    // Strip everything that isn't a Unicode letter or digit. Tax IDs are
    // identity numbers; separators/formatting carry no meaning.
    return normalized.replace(/[^\p{L}\p{N}]+/gu, '');
  }
  // Loan ID: strip ALL whitespace, fold case (matching is case-insensitive), then
  // drop leading zeros (fixed-width zero-padding is not significant) — but never to
  // empty, so an all-zeros ID collapses to "0" rather than a blank. Trailing/internal
  // digits and non-whitespace separators are preserved, so `0012345`, `12345`, and
  // `12 345` unify while genuinely distinct IDs stay distinct. `toLowerCase` (not
  // `toLocaleLowerCase`) folds via the locale-independent Unicode default mapping.
  return normalized
    .replace(/\s+/gu, '')
    .toLowerCase()
    .replace(/^0+(?=.)/u, '');
}

const BASE64URL_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

/** Reverse lookup: base64url char → 6-bit value. -1 for any non-alphabet char. */
const BASE64URL_LOOKUP: readonly number[] = (() => {
  const table = new Array<number>(128).fill(-1);
  for (let i = 0; i < BASE64URL_ALPHABET.length; i += 1) {
    table[BASE64URL_ALPHABET.charCodeAt(i)] = i;
  }
  return table;
})();

/**
 * Encode bytes as unpadded base64url (RFC 4648 §5). Isomorphic: pure bit-twiddle
 * over the byte array, no `btoa`/`Buffer`. Output is URL-safe and padding-free,
 * so it is stable across storage and transport.
 */
export function base64UrlEncode(bytes: Uint8Array): string {
  let out = '';
  let i = 0;
  for (; i + 2 < bytes.length; i += 3) {
    // Indices i, i+1, i+2 are all in-bounds in this branch; assert via `as
    // number` after the bounds-guaranteeing loop condition so
    // noUncheckedIndexedAccess doesn't widen them to `number | undefined`.
    const b0 = bytes[i] as number;
    const b1 = bytes[i + 1] as number;
    const b2 = bytes[i + 2] as number;
    const triple = (b0 << 16) | (b1 << 8) | b2;
    out +=
      BASE64URL_ALPHABET[(triple >> 18) & 0x3f]! +
      BASE64URL_ALPHABET[(triple >> 12) & 0x3f]! +
      BASE64URL_ALPHABET[(triple >> 6) & 0x3f]! +
      BASE64URL_ALPHABET[triple & 0x3f]!;
  }
  const remaining = bytes.length - i;
  if (remaining === 1) {
    const b0 = bytes[i] as number;
    const triple = b0 << 16;
    out += BASE64URL_ALPHABET[(triple >> 18) & 0x3f]! + BASE64URL_ALPHABET[(triple >> 12) & 0x3f]!;
  } else if (remaining === 2) {
    const b0 = bytes[i] as number;
    const b1 = bytes[i + 1] as number;
    const triple = (b0 << 16) | (b1 << 8);
    out +=
      BASE64URL_ALPHABET[(triple >> 18) & 0x3f]! +
      BASE64URL_ALPHABET[(triple >> 12) & 0x3f]! +
      BASE64URL_ALPHABET[(triple >> 6) & 0x3f]!;
  }
  return out;
}

/**
 * Decode unpadded base64url back to bytes. Tolerates trailing `=` padding (some
 * producers emit it) but never requires it. Throws on any character outside the
 * base64url alphabet, or on a length that can't encode whole bytes (a stray
 * single trailing char), so a malformed key surfaces immediately rather than
 * decoding to garbage.
 */
export function base64UrlDecode(encoded: string): Uint8Array {
  // Drop optional padding; it carries no information for an unpadded codec.
  const input = encoded.replace(/=+$/u, '');
  const len = input.length;
  if (len % 4 === 1) {
    throw new Error('Invalid base64url string: orphaned trailing character.');
  }
  const fullGroups = Math.floor(len / 4);
  const remainder = len - fullGroups * 4;
  const outLength = fullGroups * 3 + (remainder === 0 ? 0 : remainder - 1);
  const out = new Uint8Array(outLength);

  let outIndex = 0;
  let charIndex = 0;
  const sextet = (ch: string): number => {
    const code = ch.charCodeAt(0);
    const value = code < 128 ? BASE64URL_LOOKUP[code]! : -1;
    if (value === -1) {
      throw new Error(`Invalid base64url character: ${JSON.stringify(ch)}.`);
    }
    return value;
  };

  for (let g = 0; g < fullGroups; g += 1) {
    const s0 = sextet(input[charIndex] as string);
    const s1 = sextet(input[charIndex + 1] as string);
    const s2 = sextet(input[charIndex + 2] as string);
    const s3 = sextet(input[charIndex + 3] as string);
    charIndex += 4;
    out[outIndex] = (s0 << 2) | (s1 >> 4);
    out[outIndex + 1] = ((s1 & 0x0f) << 4) | (s2 >> 2);
    out[outIndex + 2] = ((s2 & 0x03) << 6) | s3;
    outIndex += 3;
  }

  if (remainder === 2) {
    const s0 = sextet(input[charIndex] as string);
    const s1 = sextet(input[charIndex + 1] as string);
    out[outIndex] = (s0 << 2) | (s1 >> 4);
  } else if (remainder === 3) {
    const s0 = sextet(input[charIndex] as string);
    const s1 = sextet(input[charIndex + 1] as string);
    const s2 = sextet(input[charIndex + 2] as string);
    out[outIndex] = (s0 << 2) | (s1 >> 4);
    out[outIndex + 1] = ((s1 & 0x0f) << 4) | (s2 >> 2);
  }

  return out;
}
